// src/components/pages/watch/WatchPage.js
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import { buildPosterImageAttrs } from "../../../utils/imageUrl";
import { getMainScrollContainer } from "../../../utils/layoutShell";
import { useSeoRoute } from "../../../seo";

const PAGE_TOP_PADDING = 4;
const PAGE_BOTTOM_PADDING = 4;
const HEADER_RAIL_MIN_HEIGHT = 58;

function SectionHeader({ title, subtitle, trailing }) {
  return (
    <Box
      sx={{
        minHeight: HEADER_RAIL_MIN_HEIGHT,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.12 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {trailing ? <Box sx={{ flexShrink: 0, pt: 0.2 }}>{trailing}</Box> : null}
    </Box>
  );
}

function LoadingState({ message }) {
  return (
    <Box
      sx={{
        minHeight: 320,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

function EmptyState({ title, subtitle }) {
  return (
    <Box sx={{ py: 5, textAlign: "center" }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

const WatchPage = () => {
  useSeoRoute();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState({
    status: "loading",
    series: [],
  });

  useLayoutEffect(() => {
    const mainScrollContainer = getMainScrollContainer();
    if (mainScrollContainer) {
      mainScrollContainer.scrollTop = 0;
      mainScrollContainer.scrollLeft = 0;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    setPageState({ status: "loading", series: [] });

    (async () => {
      try {
        const seriesSnap = await getDocs(collection(db, "series"));

        if (cancelled) return;

        const series = seriesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        setPageState({
          status: "ready",
          series,
        });
      } catch (error) {
        console.error("Error loading watch content:", error);
        if (!cancelled) {
          setPageState({ status: "error", series: [] });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const { status, series } = pageState;

  const playSeries = (seriesId) => {
    navigate(`/series/${seriesId}`);
  };

  const renderSeries = () => {
    if (status === "loading") {
      return <LoadingState message="Loading series catalog..." />;
    }

    if (status === "error") {
      return (
        <EmptyState
          title="Couldn’t load series"
          subtitle="Please try again in a moment."
        />
      );
    }

    if (series.length === 0) {
      return (
        <EmptyState
          title="No series available yet"
          subtitle="New episodes are on the way."
        />
      );
    }

    return (
      <>
        <SectionHeader title="Binge Series" subtitle="Jump into seasons and episodes" />

        <Grid container spacing={2}>
          {series.map((seriesItem) => {
            const seriesPosterAttrs = buildPosterImageAttrs(seriesItem.landscapePosterUrl, {
              sizes: "(max-width: 600px) 50vw, (max-width: 1200px) 33vw, 25vw",
            });

            return (
              <Grid item xs={6} sm={6} md={4} lg={3} key={seriesItem.id}>
                <Card
                  onClick={() => playSeries(seriesItem.id)}
                  elevation={0}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 1.4,
                    backgroundColor: "transparent",
                    transition: "transform 160ms ease",
                    "&:hover": { transform: "translateY(-2px)" },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={seriesPosterAttrs.src}
                    srcSet={seriesPosterAttrs.srcSet || undefined}
                    sizes={seriesPosterAttrs.sizes || undefined}
                    loading="lazy"
                    decoding="async"
                    alt={seriesItem.title}
                    sx={{
                      width: "100%",
                      aspectRatio: "16/9",
                      objectFit: "cover",
                      borderRadius: 1.4,
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 10px 24px rgba(0,0,0,0.3)"
                          : "0 10px 24px rgba(15, 42, 115, 0.16)",
                    }}
                  />
                  <CardContent sx={{ p: 0, mt: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                      {seriesItem.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {seriesItem.description || "Open to browse episodes."}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </>
    );
  };

  const countLabel = `${series.length} series`;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        bgcolor: "background.default",
        color: "text.primary",
        overflowAnchor: "none",
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorY: "contain",
      }}
    >
      <Box
        sx={(theme) => ({
          position: "relative",
          px: 2,
          pb: `calc(${theme.spacing(PAGE_BOTTOM_PADDING)} + env(safe-area-inset-bottom))`,
          pt: PAGE_TOP_PADDING,
          width: "100%",
          maxWidth: 920,
          mx: "auto",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -70,
            left: -24,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background:
              theme.palette.mode === "dark"
                ? "radial-gradient(circle, rgba(76,150,255,0.22) 0%, rgba(76,150,255,0) 72%)"
                : "radial-gradient(circle, rgba(76,150,255,0.18) 0%, rgba(76,150,255,0) 72%)",
            pointerEvents: "none",
          },
        })}
      >
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <SectionHeader
            title="Watch"
            subtitle="Binge-worthy series and new episodes"
            trailing={
              <Chip
                label={countLabel}
                size="small"
                sx={(theme) => ({
                  fontWeight: 700,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.text.primary, 0.08)
                      : alpha(theme.colors.blue, 0.08),
                })}
              />
            }
          />

          {renderSeries()}
        </Box>
      </Box>
    </Box>
  );
};

export default WatchPage;
